// HomeStatStrip — 4-tile stats row matching the prototype's StatsRow.
//
// Bound to useHomeStats(). Each HomeStat already provides:
//   - eyebrow  → .lab
//   - value    → .val (with optional unit if present in caption)
//   - trend pill (success/danger) → .delta.pos / .delta.neg
//   - neutral trend or caption    → .delta (default)
//
// Splits the value into number + unit for the prototype's typographic
// treatment when the value contains a "/" (e.g. "3 / 5") or a "%".

import { useHomeStats, type HomeStat } from "./useHomeStats.ts";

interface SplitValue {
  main: string;
  unit: string;
}

function splitValue(value: string): SplitValue {
  // Heuristic: split off "%" or "/ X" tail to render with .unit treatment.
  const slashIdx = value.indexOf("/");
  if (slashIdx > 0) {
    return {
      main: value.slice(0, slashIdx).trim(),
      unit: value.slice(slashIdx).trim(),
    };
  }
  if (value.endsWith("%")) {
    return { main: value.slice(0, -1).trim(), unit: "%" };
  }
  return { main: value, unit: "" };
}

function deltaClass(trendTone: HomeStat["trendTone"]): string {
  if (trendTone === "success") return "pos";
  if (trendTone === "danger") return "neg";
  return "";
}

export function HomeStatStrip(): JSX.Element {
  const stats = useHomeStats();
  return (
    <div className="stats-row">
      {stats.map((stat) => {
        const { main, unit } = splitValue(stat.value);
        const delta = stat.trendText ?? stat.caption;
        return (
          <div className="stat" key={stat.key}>
            <div className="lab">{stat.eyebrow}</div>
            <div className="val">
              {main}
              {unit !== "" && <span className="unit">{unit}</span>}
            </div>
            {delta !== undefined && delta !== "" && (
              <div className={`delta ${deltaClass(stat.trendTone)}`}>{delta}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
